import _ from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Formik, Form, FormikProps } from 'formik'
import * as yup from 'yup'
import { skipToken } from '@reduxjs/toolkit/query'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import SelectField from '@/components/formik/fields/SelectField'
import SelectInput from '@/components/ui/inputs/SelectInput'
import TextField from '@/components/formik/fields/TextField'

import { ExpertiseSpecialities } from '@/constants/specialities'

import { useIntro } from '@/hooks/useIntro'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'

import NotFoundPage from '@/pages/error/NotFoundPage'

import { ExpertiseService, ExpertiseStatus } from '@/core/models/expertise.model'
import { useDeleteExpertiseMutation, useDraftExpertiseMutation, useGetExpertiseQuery, useUpdateExpertiseMutation } from '@/core/services/expertise'
import { ChatModal } from '@/features/medconnect-agent/ui/ChatModal'
import { useGetChatRoomQuery } from '@/core/services/chat-room'
import { useGenerateExpertiseMessageMutation } from '@/core/services/medconnect-agent-service'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { UpdateExpertiseIntro } from '@/utils/onboarding/intro'

import Documents from './containers/Documents'
import ExpertiseHeader from './components/ExpertiseHeader'
import HealthDataList from './containers/HealthDataList'
import PatientInfosCard from './components/PatientInfosCard'
import SelectTranscriptionDialog from './containers/SelectTranscriptionDialog'
import Transcriptions from './containers/Transcriptions'
import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import ExpertiseForms from './containers/ExpertiseForms'
import { MedicalActsSection } from '@/features/medical-acts/ui/MedicalActsSection'

type UpdateExpertiseFormValues = {
  comment: string
  speciality: string
}

export default function ExpertiseUpdatePage() {
  const navigate = useNavigate()
  const { expertiseId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t, i18n } = useTranslation()

  const isTooSmall = useIsMobile()
  const dir = isTooSmall ? 'column' : 'row'
  const align = isTooSmall ? 'flex-end' : 'center'

  const formikRef = useRef<FormikProps<UpdateExpertiseFormValues>>(null)

  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState<boolean>(false)
  const [elaraChatOpen, setElaraChatOpen] = useState<boolean>(false)
  const [elaraSessionId, setElaraSessionId] = useState<string | null>(null)
  const lastFilledMessageIdRef = useRef<string | null>(null)

  const authUser = useAppSelector(selectAuthUser)

  const { data: expertise, isLoading: isFetchingExpertise } = useGetExpertiseQuery(expertiseId ?? skipToken)
  const [deleteExpertise, { isLoading: isDeletingExpertise }] = useDeleteExpertiseMutation()
  const [draftExpertise, { isLoading: isDraftingExpertise }] = useDraftExpertiseMutation()
  const [updateExpertise, { isLoading: isUpdatingExpertise }] = useUpdateExpertiseMutation()
  const [generateExpertiseMessage, { isLoading: isGeneratingMessage }] = useGenerateExpertiseMessageMutation()

  const isDraft = expertise?.status === 'draft'

  const isExpertiseAssociatedWithExternalService = useMemo(() => _.includes([ExpertiseStatus.Omnidoc], expertise?.status), [expertise])

  const serviceOptions = useMemo(
    () =>
      Object.keys(ExpertiseService).map((key) => ({
        label: key,
        value: ExpertiseService[key as keyof typeof ExpertiseService],
      })),
    []
  )
  const [service, setService] = useState<string>(serviceOptions[0].value)

  const updateExpertiseFormInitialValues = useMemo(
    () => ({
      comment: expertise?.comment || '',
      speciality: expertise?.speciality.toUpperCase() || '',
    }),
    [expertise]
  )

  const updateExpertiseFormValidationSchema = useMemo(
    () =>
      yup.object().shape({
        comment: service === ExpertiseService.MedConnect && !isExpertiseAssociatedWithExternalService ? yup.string().required().min(3) : yup.string(),
        speciality: yup.string(),
      }),
    [isExpertiseAssociatedWithExternalService, service]
  )

  const handleDeleteExpertise = async () => {
    if (!expertiseId) return
    try {
      await deleteExpertise(expertiseId).unwrap()
      enqueueSnackbar(t('notification.expertise.delete.success'), { variant: 'success' })
      navigate('/expertises')
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.delete.error')), { variant: 'error' })
    }
  }

  const handleGenerateExpertiseMessage = async () => {
    if (!expertiseId) return
    try {
      const lang = i18n.language.toLowerCase()
      const language = lang.startsWith('fr') || lang.startsWith('iv') ? 'French' : 'English'
      const result = await generateExpertiseMessage({ expertiseId, includeHistory: true, language }).unwrap()
      if (result.success && result.message) {
        formikRef.current?.setFieldValue('comment', result.message, false)
        enqueueSnackbar(t('page.expertise.generateMessage.success'), { variant: 'success' })
      } else {
        enqueueSnackbar(t('page.expertise.generateMessage.error'), { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(t('page.expertise.generateMessage.error'), { variant: 'error' })
    }
  }

  const handleConfirmation = async (values: UpdateExpertiseFormValues) => {
    if (!expertiseId) return

    if (isDraft) {
      try {
        await draftExpertise({ id: expertiseId, comment: values.comment, speciality: values.speciality }).unwrap()
        navigate(`/expertises/${expertiseId}/validation`, { state: { service } })
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.draft.error')), { variant: 'error' })
      }
    } else {
      try {
        await updateExpertise({ id: expertiseId, speciality: values.speciality }).unwrap()
        navigate(`/expertises/${expertiseId}`)
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, t('notification.expertise.update.error')), { variant: 'error' })
      }
    }
  }

  const handleCancel = () => {
    if (isDraft) {
      navigate('/expertises')
    } else if (expertiseId) {
      navigate(`/expertises/${expertiseId}`)
    }
  }

  const handleChangeService = (event: React.ChangeEvent<HTMLInputElement>) => {
    setService(event.target.value)
    formikRef.current?.setFieldTouched('comment', false, false)
  }

  const toggleDeleteConfirmationDialog = () => {
    setDeleteConfirmationDialogOpen((prevState) => !prevState)
  }

  useIntro(UpdateExpertiseIntro, !expertise)


  useEffect(() => {
    if (isExpertiseAssociatedWithExternalService && expertiseId) {
      navigate(`/expertises/${expertiseId}`)
    }
  }, [isExpertiseAssociatedWithExternalService, expertiseId])

  // show loader when fetching expertise
  if (isFetchingExpertise) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  // show not found if expertise is not existed or authenticated user is not its creator
  if (!expertise || authUser?._id !== expertise.createdBy._id) {
    return <NotFoundPage />
  }

  return (
    <Container maxWidth="xl" sx={{ height: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)` }}>
      <Box pt="30px" pb="30px" height="100%">
        <Card sx={{ borderRadius: 3, height: '100%' }}>
          <Stack sx={{ height: '100%' }}>
            <Stack direction={dir} alignItems={align} justifyContent="space-between" p={3}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
                <Typography variant="h2" component="div" noWrap={isTooSmall ? false : true} sx={{ fontSize: '24px', fontWeight: 700 }}>
                  {t('page.expertise.title', { context: isDraft ? 'create' : 'update' })}
                </Typography>
                {isDraft && expertise?.chatRoom && (
                  <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => setElaraChatOpen(true)} size="small">
                    {t('page.expertise.askElara.button')}
                  </Button>
                )}
              </Stack>
              <Stack alignItems="center" justifyContent="flex-end" direction="row" spacing={2}>
                <ExpertiseHeader
                  isDoctor={expertise.medecin?._id === authUser?._id}
                  speciality={expertise.speciality}
                  status={expertise.status}
                  startDate={expertise.startDate}
                  endDate={expertise.endDate}
                />
                <IconButton onClick={() => navigate('/expertises')} size="small" data-testid="close-button">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
            {service === ExpertiseService.Omnidoc && (
              <Alert icon={false} severity="warning" sx={{ ml: 3, mr: 3, mb: 2 }} variant="outlined">
                {t('page.expertise.omnidocWarningMessage')}
              </Alert>
            )}
            <Box sx={{ flex: '1 1 auto', pl: 3, pr: 3, pb: 2, overflowY: 'auto' }}>
              <Grid container spacing={5}>
                <Grid item xs={12} sm={7} md={8} xl={9}>
                  <Box sx={{ width: '100%', pt: '10px', pb: '20px' }}>
                    <Stack direction="column" spacing={5}>
                      {isDraft && (
                        <Stack direction="column" justifyContent="flex-end" alignItems="start" spacing={2} id="service">
                          <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
                            {t('page.expertise.service.title')}
                          </Typography>
                          <SelectInput onChange={handleChangeService} options={serviceOptions} value={service} />
                        </Stack>
                      )}
                      <HealthDataList id="patient-data" />
                      <div id="expertise-document">
                        <Documents />
                      </div>
                      <div id="expertise-forms">
                        <ExpertiseForms expertiseId={expertiseId!} />
                      </div>
                      <div id="expertise-medical-acts">
                        <MedicalActsSection patientId={expertise.patient._id} expertiseId={expertiseId} showTitle />
                      </div>
                      {isDraft && <Transcriptions />}
                      <Formik
                        innerRef={formikRef}
                        initialValues={updateExpertiseFormInitialValues}
                        onSubmit={handleConfirmation}
                        validationSchema={updateExpertiseFormValidationSchema}
                      >
                        {(formikProps) => (
                          <Form onSubmit={formikProps.handleSubmit} id="extra-infos">
                            <Stack spacing={3}>
                              <Stack justifyContent="flex-end" alignItems="start" spacing={2}>
                                <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
                                  {t('page.expertise.speciality.title')}
                                </Typography>
                                <Box sx={{ width: '40%' }}>
                                  <SelectField
                                    fullWidth
                                    name="speciality"
                                    data-testid="speciality-select"
                                    options={_.map(ExpertiseSpecialities, (speciality) => ({
                                      label: speciality.toUpperCase(),
                                      value: speciality.toUpperCase(),
                                    }))}
                                  />
                                </Box>
                              </Stack>
                              {isDraft && (
                                <Stack direction="column" justifyContent="flex-end" alignItems="start" spacing={2}>
                                  <Typography variant="h3" component="div" noWrap sx={{ color: '#6B7280', fontSize: '16px', fontWeight: 700 }}>
                                    {t('page.expertise.initialMessage.title')}
                                  </Typography>
                                  <Stack direction={dir} spacing={2} width="100%">
                                    <Box flex={1}>
                                      <TextField
                                        fullWidth
                                        inputProps={{ 'data-testid': 'comment-textarea' }}
                                        maxRows={6}
                                        minRows={4}
                                        multiline
                                        name="comment"
                                      />
                                    </Box>
                                    <Box>
                                      <Stack direction="row" spacing={1}>
                                        <LoadingButton
                                          startIcon={<AutoAwesomeIcon />}
                                          loading={isGeneratingMessage}
                                          onClick={handleGenerateExpertiseMessage}
                                          variant="outlined"
                                          size="small"
                                          sx={{ height: '40px' }}
                                        >
                                          {t('page.expertise.generateMessage.button')}
                                        </LoadingButton>
                                        <SelectTranscriptionDialog
                                          onSelect={(sentence) => {
                                            formikProps.setFieldValue('comment', sentence)
                                          }}
                                        />
                                      </Stack>
                                    </Box>
                                  </Stack>
                                </Stack>
                              )}
                            </Stack>
                          </Form>
                        )}
                      </Formik>
                    </Stack>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={5} md={4} xl={3}>
                  <Box sx={{ position: 'sticky', top: 0, pt: '10px' }}>
                    <PatientInfosCard patient={expertise.patient} />
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between" pt={2} pb={2} pl={3} pr={3}>
              <Stack direction="row" alignItems="center" justifyContent="end" spacing={3} sx={{ flex: 1 }}>
                {!isDraft && (
                  <Button onClick={handleCancel} variant="neutral">
                    {t('common:button.cancel.label')}
                  </Button>
                )}
                <LoadingButton
                  color="primary"
                  loading={isDraftingExpertise || isUpdatingExpertise}
                  onClick={() => formikRef.current?.submitForm()}
                  variant="contained"
                  id="confirm-button"
                >
                  {t('common:button:confirm.label')}
                </LoadingButton>
                {isDraft && (
                  <Button color="error" onClick={toggleDeleteConfirmationDialog} variant="contained">
                    {t('common:button:delete.label')}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Card>
      </Box>
      <DeleteConfirmationDialog
        isDeleting={isDeletingExpertise}
        onClose={toggleDeleteConfirmationDialog}
        onDelete={handleDeleteExpertise}
        open={deleteConfirmationDialogOpen}
        title={t('dialog.deleteConfirmation.expertise.title')}
        message={t('dialog.deleteConfirmation.expertise.message')}
      />
      <ChatModal
        open={elaraChatOpen}
        onClose={() => {
          setElaraChatOpen(false)
          // Reset the last filled message ID when closing the modal
          lastFilledMessageIdRef.current = null
        }}
        sessionId={elaraSessionId}
        onSessionIdChange={setElaraSessionId}
        titleKey="page.expertise.elaraChat.title"
        context={expertise ? { expertiseId: expertise._id, patientId: expertise.patient._id } : undefined}
        onMessageGenerated={(message, messageId) => {
          // Fill the "Initial message" field with the generated message
          // Only if this message hasn't been used to fill the form yet
          if (formikRef.current && isDraft && lastFilledMessageIdRef.current !== messageId) {
            lastFilledMessageIdRef.current = messageId
            formikRef.current.setFieldValue('comment', message, false)
          }
        }}
      />
    </Container>
  )
}
